import { store } from 'store/store';
import { AlarmsApi } from './Alarms.api';

/**
 * @file Keeps the `getAlarms` RTK-Query cache in sync with real-time alarm SSE
 * events (`alarm-add`, `alarm-update`, `alarm-remove`) without triggering a
 * network refetch. Each exported handler mutates every cached `getAlarms` query
 * whose filters still match the incoming alarm.
 *
 * The alarm stream itself is not workplace-scoped, so each handler takes the
 * caller's workplace id and ignores alarms that belong to another one. Without
 * that the list shows one row per workplace for a single weather alert.
 */

/**
 * Coerces a raw alarm payload into the shape the UI expects.
 *
 * Numeric fields (`id`, `state`, `silenced`) are forced to numbers so strict
 * comparisons against query args succeed, and `acknowledged` is normalized to an
 * array. Returns the input unchanged if it is falsy or if normalization throws.
 *
 * @param {Object} a - Raw alarm object from an SSE payload.
 * @returns {Object} A shallow copy of the alarm with normalized fields, or the
 *   original value if it could not be normalized.
 */
const normalizeAlarm = (a) => {
  if (!a) return a;
  try {
    const alarm = { ...a };
    if ('id' in alarm) alarm.id = Number(alarm.id);
    if ('state' in alarm) alarm.state = Number(alarm.state);
    if ('silenced' in alarm) alarm.silenced = Number(alarm.silenced);
    if (!Array.isArray(alarm.acknowledged))
      alarm.acknowledged = alarm.acknowledged ? [alarm.acknowledged] : [];
    return alarm;
  } catch (err) {
    console.error('normalizeAlarm error', err, a);
    return a;
  }
};

/**
 * Determines whether an alarm satisfies the filters of a given cached query.
 *
 * Used to decide whether an alarm should appear in (or be removed from) a
 * specific `getAlarms` cache entry. A null/undefined `args` (unfiltered query)
 * matches everything.
 *
 * @param {Object} alarm - Normalized alarm.
 * @param {Object} [args] - The `originalArgs` of a cached `getAlarms` query
 *   (e.g. `{ state, silenced }`).
 * @returns {boolean} `true` if the alarm matches the query's filters.
 */
const entryMatchesQuery = (alarm, args) => {
  if (!args) return true;
  if (args.state != null && Number(args.state) !== Number(alarm.state)) return false;
  if (args.silenced != null && Number(args.silenced) !== Number(alarm.silenced)) return false;
  // Add other arg checks here if needed (search, etc.)
  return true;
};

/**
 * Normalizes an SSE alarm payload into an array.
 *
 * The backend broadcasts either one alarm or a batch of them (the expiry and
 * group-close sweeps emit arrays), so every handler works on a list.
 *
 * @param {Object|Object[]} payload - One alarm (or id holder) or many.
 * @returns {Object[]} The payload as an array.
 */
const toAlarmArray = (payload) => (Array.isArray(payload) ? payload : [payload]);

/**
 * Decides whether an alarm belongs to a different workplace than this browser's.
 *
 * The alarm stream is not workplace-scoped on the backend: one Custom Weather or
 * FEMA alert produces one alarm per workplace and broadcasts all of them to every
 * connection, which puts N copies of one message on the Pending Alarms screen.
 * The list read (`GET /v2/alarm`) is scoped, so the cache must be too.
 *
 * Drops nothing unless both sides are known: an unknown workplace (no cookie) or
 * a payload without `organization_id` is kept, because guessing would hide the
 * user's own alarms.
 *
 * @param {Object} alarm - Normalized alarm.
 * @param {number|null} [workplaceId] - This browser's workplace id.
 * @returns {boolean} `true` when the alarm is known to belong elsewhere.
 */
const isOtherWorkplace = (alarm, workplaceId) =>
  Number.isFinite(workplaceId) &&
  alarm?.organization_id != null &&
  Number(alarm.organization_id) !== Number(workplaceId);

/**
 * Determines whether a payload is a complete alarm rather than a partial update.
 *
 * `alarm-update` carries whatever the writing statement had to hand — a silence
 * sends `{ id, silenced, silenced_by }`, the expiry sweep sends
 * `{ id, expire_time, info }`. Only a full alarm may create a row; a partial one
 * merges into a row that already exists or is ignored.
 *
 * @param {Object} alarm - Normalized alarm.
 * @returns {boolean} `true` when the payload carries a whole alarm.
 */
const isFullAlarm = (alarm) =>
  !!alarm && alarm.organization_id != null && alarm.info != null && alarm.activity != null;

/**
 * Resolves the alarms an event applies to: normalized, identifiable and in this
 * workplace.
 *
 * @param {Object|Object[]} payload - The alarm(s) carried by the event.
 * @param {number|null} [workplaceId] - This browser's workplace id.
 * @returns {Object[]} The alarms that may touch the cache.
 */
const resolveEventAlarms = (payload, workplaceId) =>
  toAlarmArray(payload)
    .map(normalizeAlarm)
    .filter((alarm) => alarm && alarm.id && !isOtherWorkplace(alarm, workplaceId));

/**
 * Iterates over every active `getAlarms` cache entry, invoking `fn` with each
 * entry's `originalArgs`.
 *
 * Lets a single SSE event update all in-flight variants of the alarms query
 * (different state/silenced/pagination filters). No-ops if the Alarms API slice
 * is not yet present in the store.
 *
 * @param {(args: Object) => void} fn - Callback receiving the cached query's
 *   `originalArgs`.
 * @returns {void}
 */
const forEachGetAlarmsCacheEntry = (fn) => {
  const state = store.getState()[AlarmsApi.reducerPath];
  if (!state) return;
  const queries = state.queries || {};
  Object.values(queries).forEach((entry) => {
    if (!entry || entry.endpointName !== AlarmsApi.endpoints.getAlarms.name) return;
    const originalArgs = entry.originalArgs;
    fn(originalArgs);
  });
};

/**
 * Safely applies a mutation to a single `getAlarms` cache entry via
 * `updateQueryData`.
 *
 * Resolves the alarm list and optional `meta` across the response shapes the
 * endpoint may produce (a bare array, `{ data: [] }`, or `{ results: [] }`) and
 * hands them to `updater` for in-place (Immer draft) mutation. Bails out
 * silently if the shape is unrecognized, and swallows errors thrown when the
 * cache entry no longer exists.
 *
 * @param {import('redux').Dispatch} dispatch - The Redux dispatch function.
 * @param {Object} args - The cached query's `originalArgs` identifying the entry.
 * @param {(list: Object[], meta: Object|null, draft: Object) => void} updater -
 *   Mutates the alarm list (and `meta`) in place.
 * @returns {void}
 */
const updateCacheForArgs = (dispatch, args, updater) => {
  try {
    dispatch(
      AlarmsApi.util.updateQueryData(AlarmsApi.endpoints.getAlarms.name, args, (draft) => {
        if (!draft) return;

        // Support multiple possible response shapes: { data: [] } or { results: [] } or direct array
        let list = null;
        let meta = null;
        if (Array.isArray(draft)) {
          list = draft;
        } else if (Array.isArray(draft.data)) {
          list = draft.data;
          meta = draft.meta;
        } else if (Array.isArray(draft.results)) {
          list = draft.results;
          meta = draft.meta;
        } else {
          // nothing we can do
          return;
        }

        updater(list, meta, draft);
      }),
    );
  } catch (err) {
    // updateQueryData can throw if the cache entry is not present anymore — ignore
    console.debug('[alarms.service] updateCacheForArgs skipped', err);
  }
};

/**
 * Handles an `alarm-add` SSE event by inserting (or merging) the alarm into
 * every matching cached `getAlarms` query.
 *
 * If the alarm already exists in an entry it is merged; otherwise it is
 * prepended and the entry's `meta.resultCount` is incremented. Entries whose
 * filters the alarm does not match are skipped. Alarms belonging to another
 * workplace are dropped. No-ops on missing `dispatch`/`eventData` or an alarm
 * without an `id`.
 *
 * @param {import('redux').Dispatch} dispatch - The Redux dispatch function.
 * @param {Object|Object[]} eventData - SSE payload; the alarm(s) may be at
 *   `eventData.data` or be `eventData` itself.
 * @param {Object} [options] - Handler options.
 * @param {number|null} [options.workplaceId] - This browser's workplace id, or
 *   `null` when it is unknown (nothing is dropped then).
 * @returns {void}
 */
export const addAlarmFromEvent = (dispatch, eventData, options = {}) => {
  if (!dispatch || !eventData) return;
  const payload = eventData?.data ?? eventData;
  const alarms = resolveEventAlarms(payload, options?.workplaceId ?? null);
  if (!alarms.length) return;

  forEachGetAlarmsCacheEntry((args) => {
    const matching = alarms.filter((alarm) => entryMatchesQuery(alarm, args));
    if (!matching.length) return;

    updateCacheForArgs(dispatch, args, (list, meta) => {
      matching.forEach((alarm) => {
        const idx = list.findIndex((a) => Number(a.id) === Number(alarm.id));
        if (idx >= 0) {
          list[idx] = { ...list[idx], ...alarm };
        } else {
          list.unshift(alarm);
          if (meta && typeof meta.resultCount === 'number') meta.resultCount += 1;
        }
      });
    });
  });
};

/**
 * Handles an `alarm-update` SSE event by reconciling the alarm against every
 * cached `getAlarms` query.
 *
 * For each entry: if the alarm is present and still matches the filters it is
 * merged; if present but no longer matches it is removed (decrementing
 * `meta.resultCount`); if absent but now matches it is prepended (incrementing
 * `meta.resultCount`) — but only when the payload is a full alarm, since a
 * partial update for an id this workplace never had must not invent a row.
 * Alarms belonging to another workplace are dropped. No-ops on missing
 * `dispatch`/`eventData` or an alarm without an `id`.
 *
 * @param {import('redux').Dispatch} dispatch - The Redux dispatch function.
 * @param {Object|Object[]} eventData - SSE payload; the alarm(s) may be at
 *   `eventData.data` or be `eventData` itself.
 * @param {Object} [options] - Handler options.
 * @param {number|null} [options.workplaceId] - This browser's workplace id, or
 *   `null` when it is unknown (nothing is dropped then).
 * @returns {void}
 */
export const applyAlarmUpdatesFromEvent = (dispatch, eventData, options = {}) => {
  if (!dispatch || !eventData) return;
  const payload = eventData?.data ?? eventData;
  const alarms = resolveEventAlarms(payload, options?.workplaceId ?? null);
  if (!alarms.length) return;

  forEachGetAlarmsCacheEntry((args) => {
    updateCacheForArgs(dispatch, args, (list, meta) => {
      alarms.forEach((alarm) => {
        const idx = list.findIndex((a) => Number(a.id) === Number(alarm.id));

        // Ask the filter about the row as it will be, not about the fragment
        // that changed: a partial update that carries no `state` or `silenced`
        // (the expiry sweep sends `{ id, expire_time, info }`) says nothing
        // about whether the row still belongs in this entry.
        const merged = idx >= 0 ? { ...list[idx], ...alarm } : alarm;
        const matches = entryMatchesQuery(merged, args);

        if (idx >= 0) {
          if (matches) {
            // merge updates
            list[idx] = { ...list[idx], ...alarm };
          } else {
            // no longer matches filters — remove
            list.splice(idx, 1);
            if (meta && typeof meta.resultCount === 'number')
              meta.resultCount = Math.max(0, meta.resultCount - 1);
          }
        } else if (matches && isFullAlarm(alarm)) {
          // not present but now matches — add
          list.unshift(alarm);
          if (meta && typeof meta.resultCount === 'number') meta.resultCount += 1;
        }
      });
    });
  });
};

/**
 * Handles an `alarm-remove` SSE event by deleting the alarm from every cached
 * `getAlarms` query that contains it.
 *
 * Removes the matching entry and decrements `meta.resultCount` (floored at 0).
 * Accepts a payload carrying either the alarm id directly or an object with an
 * `id` field, one or many. No-ops on missing `dispatch`/`eventData` or a falsy
 * id.
 *
 * @param {import('redux').Dispatch} dispatch - The Redux dispatch function.
 * @param {Object|Object[]|number} eventData - SSE payload; the id(s) may be at
 *   `eventData.data.id`, `eventData.id`, or be the value itself.
 * @param {Object} [options] - Handler options.
 * @param {number|null} [options.workplaceId] - This browser's workplace id, or
 *   `null` when it is unknown (nothing is dropped then).
 * @returns {void}
 */
export const removeAlarmFromEvent = (dispatch, eventData, options = {}) => {
  if (!dispatch || !eventData) return;
  const workplaceId = options?.workplaceId ?? null;
  const payload = eventData?.data ?? eventData;
  const ids = toAlarmArray(payload)
    .filter((entry) => !isOtherWorkplace(entry, workplaceId))
    .map((entry) => entry?.id ?? entry)
    .filter(Boolean);
  if (!ids.length) return;

  forEachGetAlarmsCacheEntry((args) => {
    updateCacheForArgs(dispatch, args, (list, meta) => {
      ids.forEach((id) => {
        const idx = list.findIndex((a) => Number(a.id) === Number(id));
        if (idx >= 0) {
          list.splice(idx, 1);
          if (meta && typeof meta.resultCount === 'number')
            meta.resultCount = Math.max(0, meta.resultCount - 1);
        }
      });
    });
  });
};

export default {
  addAlarmFromEvent,
  applyAlarmUpdatesFromEvent,
  removeAlarmFromEvent,
};
